import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet, MatBottomSheetConfig, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ComponentType } from '@angular/cdk/portal';
import { DialogsTheme, TenantConfigService } from './tenant-config.service';
import { UnderLiquidationDialogComponent } from '../components/under-liquidation-dialog/under-liquidation-dialog.component';

export type DialogOpenTarget<T> = ComponentType<T> | TemplateRef<T>;

@Injectable({ providedIn: 'root' })
export class DialogService {
  constructor(
    private matDialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private tenantConfigService: TenantConfigService,
  ) {}

  /**
   * Open a dialog using the current tenant's dialog theme. You can optionally override the theme per call.
   */
  open<T, D = any, R = any>(
    target: DialogOpenTarget<T>,
    config?: MatDialogConfig<D> | MatBottomSheetConfig<D>,
    overrideTheme?: DialogsTheme,
  ): MatDialogRef<T, R> {
    const theme = overrideTheme || this.tenantConfigService.tenant.dialogsTheme;
    switch (theme) {
      case DialogsTheme.ASIDE:
        return this.openAside(target, config as MatDialogConfig<D>);
      case DialogsTheme.BOTTOM_SHEET:
        return this.openBottomSheet(target, config as MatBottomSheetConfig<D>);
      case DialogsTheme.MODAL:
      default:
        return this.openModal(target, config as MatDialogConfig<D>);
    }
  }

  /**
   * Opens the Under Liquidation notification popup.
   */
  openUnderLiquidationDialog(): MatDialogRef<UnderLiquidationDialogComponent, any> {
    return this.open(UnderLiquidationDialogComponent, {
      width: '460px',
      maxWidth: '92vw',
      disableClose: false,
    }, DialogsTheme.MODAL);
  }

  closeAll(): void {
    this.matDialog.closeAll();
    this.bottomSheet.dismiss();
  }

  private openModal<T, D = any, R = any>(
    target: DialogOpenTarget<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    const finalConfig: MatDialogConfig<D> = {
      width: '600px',
      height: 'auto',
      ...config,
    };
    return this.matDialog.open(target as any, finalConfig);
  }

  private openAside<T, D = any, R = any>(
    target: DialogOpenTarget<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    const isRtl = (document && document.dir === 'rtl');
    const position = isRtl ? { top: '0', left: '0' } : { top: '0', right: '0' };
    const panelClass = this.mergePanelClass('side-dialog', config?.panelClass);

    const finalConfig: MatDialogConfig<D> = {
      width: '600px',
      height: '100vh',
      maxHeight: '100vh',
      position,
      panelClass,
      ...config,
    };
    return this.matDialog.open(target as any, finalConfig);
  }

  private openBottomSheet<T, D = any, R = any>(
    target: DialogOpenTarget<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    const position = { bottom: '0', left: '50%', transform: 'translateX(-50%)' } ;

    const finalConfig: MatDialogConfig<D> = {
      width: '600px',
      height: 'auto',
      maxHeight: '100vh',
      position,
      ...config,
    };
    return this.matDialog.open(target as any, finalConfig);
  }

  private mergePanelClass(defaultClass: string, existing?: string | string[]): string | string[] {
    if (!existing) return defaultClass;
    if (Array.isArray(existing)) return Array.from(new Set([defaultClass, ...existing]));
    return `${defaultClass} ${existing}`.trim();
  }
}

